import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const INACTIVITY_LIMIT_MS = 25 * 60 * 1000;   // 25 min sin actividad → logout
const TOKEN_LIFETIME_MS   = 30 * 60 * 1000;   // ✅ debe coincidir con ACCESS_TOKEN_EXPIRE_MINUTES del backend
const REFRESH_BEFORE_MS   =  2 * 60 * 1000;   // refrescar 2 min ANTES de que expire

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

// ✅ FIX: Lee el campo "exp" del JWT para saber cuánto tiempo real queda,
// en lugar de asumir siempre el tiempo completo desde que se montó el componente.
const getMsUntilRefresh = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expMs = payload.exp * 1000;                        // exp está en segundos
        const msLeft = expMs - Date.now();                       // cuánto falta para expirar
        const msUntilRefresh = msLeft - REFRESH_BEFORE_MS;      // refrescar 2 min antes
        return msUntilRefresh;
    } catch {
        return null;
    }
};

export const useSessionTimer = () => {
    const navigate = useNavigate();
    const inactivityTimer = useRef(null);
    const refreshTimer    = useRef(null);
    const scheduleRefreshRef = useRef(null);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        clearTimeout(inactivityTimer.current);
        clearTimeout(refreshTimer.current);
        navigate('/login', { replace: true });
    }, [navigate]);

    const refreshToken = useCallback(async () => {
        try {
            const response = await api.post('/usuarios/refresh-token');
            localStorage.setItem('token', response.data.access_token);
            // Después de refrescar, programar el próximo refresh leyendo el nuevo exp
            scheduleRefreshRef.current?.();
        } catch {
            logout();
        }
    }, [logout]);

    // ✅ FIX: scheduleRefresh ahora calcula el delay REAL leyendo exp del token,
    // en lugar de usar siempre REFRESH_THRESHOLD_MS desde el momento de montaje.
    const scheduleRefresh = useCallback(() => {
        clearTimeout(refreshTimer.current);
        const delay = getMsUntilRefresh();

        if (delay === null) {
            logout();   // token inválido o ausente
            return;
        }

        if (delay <= 0) {
            // El token ya está a punto de expirar o expiró → refrescar inmediatamente
            refreshToken();
            return;
        }

        refreshTimer.current = setTimeout(refreshToken, delay);
    }, [refreshToken, logout]);

    scheduleRefreshRef.current = scheduleRefresh;

    const resetInactivityTimer = useCallback(() => {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(logout, INACTIVITY_LIMIT_MS);
    }, [logout]);

    useEffect(() => {
        if (!localStorage.getItem('token')) return;

        resetInactivityTimer();
        scheduleRefresh();      // ← usa exp real del token desde el primer montaje

        ACTIVITY_EVENTS.forEach(e =>
            window.addEventListener(e, resetInactivityTimer, { passive: true })
        );

        return () => {
            ACTIVITY_EVENTS.forEach(e =>
                window.removeEventListener(e, resetInactivityTimer)
            );
            clearTimeout(inactivityTimer.current);
            clearTimeout(refreshTimer.current);
        };
    }, [resetInactivityTimer, scheduleRefresh]);
};